/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Smart suppression", description = "Hold back low-impact messages and focus sends on users most likely to convert using AI Decisioning's predictive suppression.", lastUpdatedDate = new Date(1788220800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Smart suppression",
        description: "Hold back low-impact messages and focus sends on users most likely to convert using AI Decisioning's predictive suppression.",
        lastUpdatedDate: "Tue Sep 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How Smart suppression works",
          "depth": 0,
          "id": "how-smart-suppression-works"
        }, {
          "value": "The suppression decision",
          "depth": 1,
          "id": "the-suppression-decision"
        }, {
          "value": "How suppressed users re-enter the send pool",
          "depth": 1,
          "id": "how-suppressed-users-re-enter-the-send-pool"
        }, {
          "value": "What the model looks at",
          "depth": 1,
          "id": "what-the-model-looks-at"
        }, {
          "value": "How suppression differs from other exclusion mechanisms",
          "depth": 1,
          "id": "how-suppression-differs-from-other-exclusion-mechanisms"
        }, {
          "value": "Enable smart suppression",
          "depth": 0,
          "id": "enable-smart-suppression"
        }, {
          "value": "Configure suppression levels and scope",
          "depth": 0,
          "id": "configure-suppression-levels-and-scope"
        }, {
          "value": "Set the suppression level",
          "depth": 1,
          "id": "set-the-suppression-level"
        }, {
          "value": "Choose a suppression scope",
          "depth": 1,
          "id": "choose-a-suppression-scope"
        }, {
          "value": "Monitor suppression performance",
          "depth": 0,
          "id": "monitor-suppression-performance"
        }, {
          "value": "Sends over time",
          "depth": 1,
          "id": "sends-over-time"
        }, {
          "value": "Historical impact of Smart suppression",
          "depth": 1,
          "id": "historical-impact-of-smart-suppression"
        }, {
          "value": "Expected conversions: Total view",
          "depth": 1,
          "id": "expected-conversions-total-view"
        }, {
          "value": "Expected conversions: Incremental view",
          "depth": 1,
          "id": "expected-conversions-incremental-view"
        }, {
          "value": "Check if suppression is working",
          "depth": 0,
          "id": "check-if-suppression-is-working"
        }, {
          "value": "Signs to investigate",
          "depth": 1,
          "id": "signs-to-investigate"
        }, {
          "value": "Best practices",
          "depth": 0,
          "id": "best-practices"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li",
      ol: "ol",
      img: "img",
      code: "code"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and lifecycle teams optimizing active AID agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["At least one active ", _jsx(_components.a, {
                    href: "/ai-decisioning/agents",
                    children: "agent"
                  })]
                }), _jsxs("li", {
                  children: [_jsx(_components.a, {
                    href: "/ai-decisioning/agents#step-2-add-and-manage-goals",
                    children: "Goals"
                  }), " defined for that agent"]
                }), _jsx("li", {
                  children: "Sufficient message volume to generate learning data"
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Smart suppression predicts the incremental impact of each message for each user, then holds back low-value sends while letting high-impact messages through. You maintain conversions while sending fewer messages and reducing unsubscribes."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-smart-suppression-works",
        children: "How Smart suppression works"
      }), "\n", _jsx(_components.h3, {
        id: "the-suppression-decision",
        children: "The suppression decision"
      }), "\n", _jsx(_components.p, {
        children: "During each AID run, the model scores every user in the eligible audience based on how likely each send is to drive the outcome you care about. Before dispatching a message, AID applies a suppression check:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Users whose score falls in the bottom X% (your configured suppression level) are held back for that run."
        }), "\n", _jsx(_components.li, {
          children: "Users with higher scores receive the best-scoring message variant."
        }), "\n", _jsx(_components.li, {
          children: "Suppressed users are not penalized — they remain eligible and are re-evaluated in every subsequent run. Suppressed users do not count toward your send volume."
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "In plain terms: for every potential send, the model asks \"If we send this\nmessage right now, how likely is it to drive the outcome we care about?\"\nMessages scored as unlikely to drive that outcome get suppressed;\nhigher-scoring ones go through. You set the suppression level based on your\nvolume and performance goals, and the AI handles the per-user, per-message\ndecisioning."
        })
      }), "\n", _jsx(_components.h3, {
        id: "how-suppressed-users-re-enter-the-send-pool",
        children: "How suppressed users re-enter the send pool"
      }), "\n", _jsx(_components.p, {
        children: "Suppression is self-correcting — suppressed users naturally work their way back into the send pool over time:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The model tracks how recently a user has been messaged. The longer someone goes without a message, the higher their predicted score becomes, so suppression effectively undoes itself over time."
        }), "\n", _jsx(_components.li, {
          children: "Users near the filter boundary have a roughly 50/50 chance of being included on any given run, so even lower-scoring users get messaged periodically through natural variation."
        }), "\n", _jsx(_components.li, {
          children: "No user ever has a 0% chance of being sent. The system enforces a minimum send probability (currently 1%), which means every user in the audience will eventually receive a message."
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "In a typical agent, only around 3–5% of users go unsent over a 30-day window —\nand these are the lowest-scoring users who simply haven't yet come up in the\nprobabilistic draw. They remain in the pool and will be re-engaged as their\nscore improves."
        })
      }), "\n", _jsx(_components.h3, {
        id: "what-the-model-looks-at",
        children: "What the model looks at"
      }), "\n", _jsx(_components.p, {
        children: "The model draws on your existing warehouse data to form its predictions. It considers signals like:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Signal type"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Examples"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Purchase and transaction history"
            }), _jsx(_components.td, {
              children: "Recent purchases, order frequency, spend patterns"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Browsing and product interactions"
            }), _jsx(_components.td, {
              children: "Pages viewed, items added to cart, search behavior"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Message engagement"
            }), _jsx(_components.td, {
              children: "Opens, clicks, and response history for prior sends"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Recency features"
            }), _jsx(_components.td, {
              children: "Days since last message, last purchase, last session"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "User-level context"
            }), _jsx(_components.td, {
              children: "Any custom fields passed as data fields in the AID configuration"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "The richer and more current these signals are, the more precisely the model can identify which sends will drive incremental value."
      }), "\n", _jsxs(_components.p, {
        children: ["Smart suppression relies on the features passed into the AID agent as ", _jsx(_components.a, {
          href: "/ai-decisioning/user-feature-matrix",
          children: "data fields"
        }), ". At minimum, include:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Message engagement history (clicks) for the relevant channel"
        }), "\n", _jsx(_components.li, {
          children: "Recency signals (days since last click, last purchase, last session)"
        }), "\n", _jsxs(_components.li, {
          children: ["Purchase or transaction history relevant to the ", _jsx(_components.a, {
            href: "/ai-decisioning/agents#step-2-add-and-manage-goals",
            children: "goal"
          }), " being optimized"]
        }), "\n", _jsx(_components.li, {
          children: "User lifecycle stage or segment identifier, if applicable"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Suppression will function without all of these, but prediction accuracy improves meaningfully as more signals are added."
      }), "\n", _jsx(_components.h3, {
        id: "how-suppression-differs-from-other-exclusion-mechanisms",
        children: "How suppression differs from other exclusion mechanisms"
      }), "\n", _jsx(_components.p, {
        children: "Smart suppression is distinct from upstream audience filters or frequency caps:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Mechanism"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Controlled by"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Effect"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Audience filter"
            }), _jsx(_components.td, {
              children: "Sync or segment definition"
            }), _jsx(_components.td, {
              children: "User never enters the AID run"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Frequency cap"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/agents#step-3-optional-configure-scheduling",
                children: "AID agent configuration"
              })
            }), _jsx(_components.td, {
              children: "User excluded after N messages in a set window"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Smart suppression"
            }), _jsx(_components.td, {
              children: "AID model (per-run)"
            }), _jsx(_components.td, {
              children: "User enters the run but message is withheld based on predicted outcome score"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Frequency caps and Smart suppression operate independently and stack. A user must first pass the frequency cap check before entering the suppression evaluation. If a user is within their cap limit but the model predicts a low likelihood of driving the outcome, Smart suppression will still hold back the message."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "enable-smart-suppression",
        children: "Enable smart suppression"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open your ", _jsx(_components.a, {
            href: "https://app.hightouch.com/ai/flows?from=docs",
            children: _jsx(_components.strong, {
              children: "agent"
            })
          }), " and go to the ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Scroll to the ", _jsx(_components.strong, {
            children: "Smart suppression (optional)"
          }), " section and expand it."]
        }), "\n", _jsxs(_components.li, {
          children: ["Turn on the ", _jsx(_components.strong, {
            children: "Enabled"
          }), " toggle to activate suppression."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set the percentage of eligible users to send to (for example, send messages to the top ", _jsx(_components.strong, {
            children: "67%"
          }), "). Select ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/smart-suppression/aid-expected-conversions-total.png",
          alt: "Smart suppression configuration panel showing the toggle, send percentage, and expected conversions chart"
        })
      }), "\n", _jsx(_components.p, {
        children: "You'll see a predicted performance curve update once the model has enough data to estimate incremental lift. Initial calibration typically occurs over the first two weeks, as the model accumulates enough outcome data to make confident predictions. During this period, suppression rates may be higher or lower than steady-state."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Smart suppression requires at least one outcome\n", _jsx(_components.a, {
            href: "/ai-decisioning/agents#step-2-add-and-manage-goals",
            children: "goal"
          }), " defined in your\nagent. Make sure goals are configured under ", _jsx(_components.strong, {
            children: "Goals"
          }), " in the agent's\nConfiguration tab before enabling suppression."]
        })
      }), "\n", _jsx(_components.p, {
        children: "To disable Smart suppression, toggle it off in the same section. All users in the eligible audience will then receive a message regardless of predicted outcome score. This is generally not recommended for live campaigns, as it removes the engagement protection suppression provides. If you want to temporarily disable it for testing, coordinate with your Hightouch team."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "configure-suppression-levels-and-scope",
        children: "Configure suppression levels and scope"
      }), "\n", _jsx(_components.h3, {
        id: "set-the-suppression-level",
        children: "Set the suppression level"
      }), "\n", _jsx(_components.p, {
        children: "Suppression is configured as a percentile threshold — you choose to send to only the top X% of users by predicted outcome score for a given run. For example:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Threshold"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Behavior"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Send to top 80%"
            }), _jsx(_components.td, {
              children: "Broad sending with light filtering — the bottom 20% is suppressed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Send to top 50%"
            }), _jsx(_components.td, {
              children: "Balanced approach, targeting users above the median score"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Send to top 30%"
            }), _jsx(_components.td, {
              children: "Highly selective sending, prioritizing only the highest-scoring users"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "A lower send percentage (more aggressive suppression) means fewer messages sent and higher average send quality — better for protecting engagement health and unsubscribe rates. A higher send percentage means broader reach — better when volume is a priority. The right balance depends on your channel, audience, and conversion baseline."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Start conservatively (for example, send to the top 80–90%) and lower the\nthreshold gradually as your agent gathers more data. Work with your Hightouch\nteam to calibrate during setup."
        })
      }), "\n", _jsx(_components.h3, {
        id: "choose-a-suppression-scope",
        children: "Choose a suppression scope"
      }), "\n", _jsx(_components.p, {
        children: "Suppression can be configured at different levels depending on how your AID agent is set up:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Scope"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Behavior"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Agent-level"
            }), _jsx(_components.td, {
              children: "A single suppression level applies across all variants and audiences within the agent"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Feature-level"
            }), _jsx(_components.td, {
              children: "Suppression evaluated independently per feature, using stratification features to group users — useful when features target meaningfully different behaviors"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["To configure feature-level suppression, use the ", _jsx(_components.strong, {
          children: "Stratification features"
        }), " option in the Smart suppression configuration. This requires a ", _jsx(_components.a, {
          href: "/ai-decisioning/user-feature-matrix",
          children: "feature matrix"
        }), " attached to the agent. Once attached, you can select a feature from the matrix to group users — suppression is then evaluated independently within each group."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/smart-suppression/aid-ss-added-feature-matrix.png",
          alt: "Stratification features configuration showing a selected feature and per-value send targets"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After selecting a stratification feature, you can set ", _jsx(_components.strong, {
          children: "per-value send targets"
        }), " that override the default suppression level. For example, if you stratify by ", _jsx(_components.code, {
          children: "SESSION_FREQUENCY_30D"
        }), ", you could send to 90% of high-frequency users but only 60% of low-frequency users. Use ", _jsx(_components.strong, {
          children: "Add"
        }), " to define a target for each value. Values without an explicit target use the default percentage."]
      }), "\n", _jsx(_components.p, {
        children: "Work with your Hightouch team to determine which scope and feature are appropriate for your agent design."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "monitor-suppression-performance",
        children: "Monitor suppression performance"
      }), "\n", _jsxs(_components.p, {
        children: ["After enabling Smart suppression, monitor its performance in two places. Go to ", _jsx(_components.strong, {
          children: "AI Decisioning > Agents"
        }), " and select your agent. The ", _jsx(_components.strong, {
          children: "Overview"
        }), " tab shows the ", _jsx(_components.strong, {
          children: "Sends over time"
        }), " and ", _jsx(_components.strong, {
          children: "Historical impact of Smart suppression"
        }), " charts. The ", _jsx(_components.strong, {
          children: "Expected conversions"
        }), " chart lives in the ", _jsx(_components.strong, {
          children: "Smart suppression"
        }), " card on the agent's ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab."]
      }), "\n", _jsx(_components.h3, {
        id: "sends-over-time",
        children: "Sends over time"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Sends over time"
        }), " chart shows total AI Decisioning sends alongside suppressed sends on a daily basis. This lets you see the volume of messages being held back relative to what's going out, and spot any sudden shifts in suppression rate that might warrant investigation."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/smart-suppression/aid-sends-over-time.png",
          alt: "Sends over time chart showing daily send volume with suppressed sends highlighted"
        })
      }), "\n", _jsx(_components.h3, {
        id: "historical-impact-of-smart-suppression",
        children: "Historical impact of Smart suppression"
      }), "\n", _jsx(_components.p, {
        children: "This table compares actual outcomes at your current send volume against a hypothetical scenario where 100% of users were sent to. For each outcome — conversions, clicks, unsubscribes — it shows the estimated difference and statistical confidence. A negative unsubscribe number (highlighted in green) is the key signal: it means suppression is reducing fatigue without meaningfully sacrificing conversions."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/smart-suppression/aid-historical-impact.png",
          alt: "Historical impact table comparing actual outcomes to hypothetical 100% send volume"
        })
      }), "\n", _jsx(_components.h3, {
        id: "expected-conversions-total-view",
        children: "Expected conversions: Total view"
      }), "\n", _jsxs(_components.p, {
        children: ["In the ", _jsx(_components.strong, {
          children: "Smart suppression"
        }), " card on the agent's ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab, the ", _jsx(_components.strong, {
          children: "Expected conversions"
        }), " chart lets you explore the tradeoff between send volume and outcomes at different suppression levels. Use the ", _jsx(_components.strong, {
          children: "Total"
        }), " and ", _jsx(_components.strong, {
          children: "Incremental"
        }), " toggle to switch views. The ", _jsx(_components.strong, {
          children: "Total"
        }), " view shows cumulative conversions as send volume increases from 0% to 100%. The curve typically flattens well before 100%, indicating that a large share of conversions are captured by the highest-lift users, and that sending to the full audience adds relatively little incremental value."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/smart-suppression/aid-expected-conversions-total.png",
          alt: "Expected conversions Total view showing cumulative conversions curve by send volume"
        })
      }), "\n", _jsx(_components.h3, {
        id: "expected-conversions-incremental-view",
        children: "Expected conversions: Incremental view"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Incremental lift view"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Incremental"
        }), " view shows the marginal conversion impact of each additional percentage of send volume. When this line turns negative, it means sending to those additional users is predicted to reduce net conversions — typically because the messages drive unsubscribes or suppress future engagement. This view helps identify the optimal send volume for your agent: the point where incremental benefit starts to diminish or reverse."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/smart-suppression/aid-expected-conversions-incremental.png",
          alt: "Expected conversions Incremental view showing marginal conversion impact by send volume"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "check-if-suppression-is-working",
        children: "Check if suppression is working"
      }), "\n", _jsx(_components.p, {
        children: "You should generally expect to see:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Conversion rates among sent users equal to or higher than your pre-AID baseline"
        }), "\n", _jsx(_components.li, {
          children: "A stable or declining unsubscribe rate over time"
        }), "\n", _jsx(_components.li, {
          children: "Suppressed users converting at lower rates than sent users in the same window"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "signs-to-investigate",
        children: "Signs to investigate"
      }), "\n", _jsx(_components.p, {
        children: "The following patterns may indicate a suppression configuration that needs tuning:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Pattern"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Likely cause"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Suppression rate is very high and send volume is lower than expected"
            }), _jsx(_components.td, {
              children: "Suppression level may be set too aggressively"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Suppression rate near 0%"
            }), _jsx(_components.td, {
              children: "Threshold may be too low, or data signals may not be discriminating enough"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "No meaningful difference in conversion rate between sent and suppressed users"
            }), _jsx(_components.td, {
              children: "Model may need additional features or retraining"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Rising unsubscribe rates"
            }), _jsx(_components.td, {
              children: "Suppression may not be filtering aggressively enough for a particular segment"
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "best-practices",
        children: "Best practices"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Enable Smart suppression once the agent has enough message and outcome data to produce stable predictions. Predictive charts typically appear after about two weeks, but the exact timing depends on signal volume."
        }), "\n", _jsxs(_components.li, {
          children: ["Review the ", _jsx(_components.strong, {
            children: "Expected conversions"
          }), " and ", _jsx(_components.strong, {
            children: "Historical impact"
          }), " views regularly to tune your suppression threshold over time."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use ", _jsx(_components.a, {
            href: "/ai-decisioning/agents#step-3-optional-configure-scheduling",
            children: "scheduling"
          }), " alongside Smart suppression to manage quiet hours and send frequency."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use ", _jsx(_components.a, {
            href: "/ai-decisioning/tags",
            children: "tags"
          }), " to analyze which message themes and creative types perform best in ", _jsx(_components.a, {
            href: "/ai-decisioning/insights",
            children: "Insights"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/agents",
            children: "Agents"
          }), " to learn how agents use goals and messages."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/user-feature-matrix",
            children: "User feature matrix"
          }), " to configure the data fields your agent uses."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/insights",
            children: "Insights"
          }), " to review performance trends."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
